import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { GraduationCap } from 'lucide-react';
import { Button } from '@/components/common/Button';
import { Badge, StatusBadge } from '@/components/common/Badge';
import { ConfirmDialog, Modal } from '@/components/common/Modal';
import { TextField, SelectField } from '@/components/forms/Field';
import { DataTable, type Column } from '@/components/tables/DataTable';
import { FilterSelect, ListPage } from '@/components/tables/ListPage';
import { useToast } from '@/components/feedback/ToastProvider';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';
import { useTableQuery } from '@/hooks/useTableQuery';
import { useResourceActions } from '@/hooks/useResourceActions';
import { useAcademicYearPicker, useCampusPicker } from '@/hooks/usePickers';
import { gradesApi } from '@/services/api/endpoints/academic';
import { queryKeys } from '@/services/api/queryKeys';
import { useAuth } from '@/features/auth/useAuth';
import { PERMISSIONS } from '@/types/permissions';
import type { AcademicStatus, EducationLevel, Grade } from '@/types/domain';
import { humanise } from '@/utils/format';

const EDUCATION_LEVELS: { value: EducationLevel; label: string }[] = [
  { value: 'PRE_PRIMARY', label: 'Pre-primary' },
  { value: 'PRIMARY', label: 'Primary' },
  { value: 'MIDDLE', label: 'Middle' },
  { value: 'SECONDARY', label: 'Secondary' },
  { value: 'HIGHER_SECONDARY', label: 'Higher secondary' },
  { value: 'OTHER', label: 'Other' },
];

const schema = z.object({
  code: z.string().min(1, 'Code is required').max(32),
  name: z.string().min(1, 'Name is required').max(100),
  displayOrder: z.coerce.number().int().min(0, 'Cannot be negative').max(100, 'Must not exceed 100'),
  educationLevel: z.enum(['PRE_PRIMARY', 'PRIMARY', 'MIDDLE', 'SECONDARY', 'HIGHER_SECONDARY', 'OTHER']),
  description: z.string().max(500),
});

type FormValues = z.infer<typeof schema>;

const EMPTY: FormValues = {
  code: '',
  name: '',
  displayOrder: 1,
  educationLevel: 'PRIMARY',
  description: '',
};

/**
 * Grade definitions.
 *
 * There is no campus field anywhere on this screen, and that is the point: a
 * grade is defined once for the school. Where and when it runs is a grade
 * offering. The campus and year filters here narrow to grades *offered* there,
 * resolved through offerings rather than stored on the grade.
 */
export function GradesPage() {
  const { can } = useAuth();
  const toast = useToast();
  const handleApiError = useApiErrorHandler();
  const table = useTableQuery('displayOrder,asc');

  const [levelFilter, setLevelFilter] = useState<EducationLevel | ''>('');
  const [statusFilter, setStatusFilter] = useState<AcademicStatus | ''>('');
  const [yearFilter, setYearFilter] = useState('');
  const [campusFilter, setCampusFilter] = useState('');
  const [editing, setEditing] = useState<Grade | null>(null);
  const [open, setOpen] = useState(false);
  const [deleting, setDeleting] = useState<Grade | null>(null);

  const years = useAcademicYearPicker();
  const campuses = useCampusPicker();

  const params = {
    ...table.params,
    ...(levelFilter !== '' ? { educationLevel: levelFilter } : {}),
    ...(statusFilter !== '' ? { status: statusFilter } : {}),
    ...(yearFilter !== '' ? { academicYearId: yearFilter } : {}),
    ...(campusFilter !== '' ? { campusId: campusFilter } : {}),
  };

  const query = useQuery({
    queryKey: queryKeys.grades.list(params),
    queryFn: () => gradesApi.list(params),
  });

  const { statusMutation, deleteMutation, invalidateAll } = useResourceActions({
    invalidates: [queryKeys.grades.all, queryKeys.gradeOfferings.all],
    changeStatus: gradesApi.changeStatus,
    remove: gradesApi.remove,
    label: 'Grade',
  });

  const form = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: EMPTY });

  const save = useMutation({
    mutationFn: (values: FormValues) => {
      const payload = { ...values, description: values.description === '' ? undefined : values.description };
      return editing === null ? gradesApi.create(payload) : gradesApi.update(editing.id, payload);
    },
    onSuccess: () => {
      toast.success(editing === null ? 'Grade created' : 'Grade updated');
      close();
      invalidateAll();
    },
    onError: (error) => handleApiError(error, form.setError),
  });

  function openCreate() {
    setEditing(null);
    form.reset(EMPTY);
    setOpen(true);
  }

  function openEdit(grade: Grade) {
    setEditing(grade);
    form.reset({
      code: grade.code,
      name: grade.name,
      displayOrder: grade.displayOrder,
      educationLevel: grade.educationLevel,
      description: grade.description ?? '',
    });
    setOpen(true);
  }

  function close() {
    setOpen(false);
    setEditing(null);
  }

  const columns: Column<Grade>[] = [
    {
      key: 'order',
      header: 'Order',
      sortKey: 'displayOrder',
      render: (grade) => <span className="tabular-nums text-subtle">{grade.displayOrder}</span>,
    },
    {
      key: 'name',
      header: 'Name',
      sortKey: 'name',
      render: (grade) => <span className="font-medium text-ink">{grade.name}</span>,
    },
    {
      key: 'code',
      header: 'Code',
      sortKey: 'code',
      render: (grade) => <span className="font-mono text-xs">{grade.code}</span>,
    },
    {
      key: 'level',
      header: 'Education level',
      sortKey: 'educationLevel',
      render: (grade) => <Badge tone="neutral">{humanise(grade.educationLevel)}</Badge>,
    },
    {
      key: 'offerings',
      header: 'Offered',
      render: (grade) => (
        <span className="tabular-nums" title="Campus-year combinations running this grade">
          {grade.offeringCount}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      sortKey: 'status',
      render: (grade) => <StatusBadge status={grade.status} />,
    },
    {
      key: 'actions',
      header: '',
      className: 'text-right',
      render: (grade) => (
        <div className="flex justify-end gap-1">
          {can(PERMISSIONS.GRADE_UPDATE) && (
            <>
              <Button size="sm" variant="ghost" onClick={() => openEdit(grade)}>
                Edit
              </Button>
              <Button
                size="sm"
                variant="ghost"
                loading={statusMutation.isPending}
                onClick={() =>
                  statusMutation.mutate({
                    id: grade.id,
                    payload: { status: grade.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE' },
                  })
                }
              >
                {grade.status === 'ACTIVE' ? 'Deactivate' : 'Activate'}
              </Button>
            </>
          )}
          {can(PERMISSIONS.GRADE_DELETE) && (
            <Button size="sm" variant="ghost" onClick={() => setDeleting(grade)}>
              Delete
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <>
      <ListPage
        title="Grades"
        description="Academic levels, defined once for the school. Use Grade Offerings to run one at a campus."
        icon={<GraduationCap className="h-5 w-5" aria-hidden="true" />}
        action={can(PERMISSIONS.GRADE_CREATE) ? <Button onClick={openCreate}>New grade</Button> : undefined}
        search={{ value: table.searchInput, onChange: table.setSearchInput, placeholder: 'Search grades…' }}
        filters={
          <>
            <FilterSelect
              label="Level"
              value={levelFilter}
              onChange={(value) => {
                setLevelFilter(value);
                table.setPage(0);
              }}
              options={EDUCATION_LEVELS}
            />
            <FilterSelect
              label="Status"
              value={statusFilter}
              onChange={(value) => {
                setStatusFilter(value);
                table.setPage(0);
              }}
              options={[
                { value: 'ACTIVE', label: 'Active' },
                { value: 'INACTIVE', label: 'Inactive' },
              ]}
            />
            {/* These narrow to grades actually offered there, via offerings. */}
            <FilterSelect
              label="Offered in"
              value={yearFilter}
              onChange={(value) => {
                setYearFilter(value);
                table.setPage(0);
              }}
              options={(years.data ?? []).map((year) => ({ value: year.id, label: year.name }))}
            />
            <FilterSelect
              label="At campus"
              value={campusFilter}
              onChange={(value) => {
                setCampusFilter(value);
                table.setPage(0);
              }}
              options={(campuses.data ?? []).map((campus) => ({ value: campus.id, label: campus.name }))}
            />
          </>
        }
        pagination={query.data?.pagination}
        onPageChange={table.setPage}
        onSizeChange={table.setSize}
      >
        <DataTable
          columns={columns}
          rows={query.data?.data}
          rowKey={(grade) => grade.id}
          loading={query.isFetching}
          error={query.error}
          onRetry={() => void query.refetch()}
          sort={table.sort}
          onSort={table.toggleSort}
          emptyTitle="No grades yet"
          emptyDescription="Define the academic levels your school runs, in the order they progress."
          emptyIcon={<GraduationCap className="h-6 w-6" aria-hidden="true" />}
        />
      </ListPage>

      <Modal
        open={open}
        title={editing === null ? 'New grade' : `Edit ${editing.name}`}
        description="A grade belongs to the school. Offer it at a campus separately."
        onClose={close}
        footer={
          <>
            <Button variant="secondary" onClick={close}>
              Cancel
            </Button>
            <Button onClick={form.handleSubmit((values) => save.mutate(values))} loading={save.isPending}>
              Save
            </Button>
          </>
        }
      >
        <form className="space-y-4" noValidate onSubmit={form.handleSubmit((values) => save.mutate(values))}>
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              label="Code"
              required
              hint="Unique within the school"
              error={form.formState.errors.code?.message}
              {...form.register('code')}
            />
            <TextField
              label="Name"
              required
              hint="Unique within the school"
              error={form.formState.errors.name?.message}
              {...form.register('name')}
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              label="Display order"
              type="number"
              min={0}
              max={100}
              required
              hint="Position in your progression. Need not be unique."
              error={form.formState.errors.displayOrder?.message}
              {...form.register('displayOrder')}
            />
            <SelectField
              label="Education level"
              required
              error={form.formState.errors.educationLevel?.message}
              {...form.register('educationLevel')}
            >
              {EDUCATION_LEVELS.map((level) => (
                <option key={level.value} value={level.value}>
                  {level.label}
                </option>
              ))}
            </SelectField>
          </div>

          <TextField
            label="Description"
            error={form.formState.errors.description?.message}
            {...form.register('description')}
          />
        </form>
      </Modal>

      <ConfirmDialog
        open={deleting !== null}
        title="Delete grade"
        message={
          deleting === null
            ? ''
            : `Delete ${deleting.name}? This is refused while any offering or subject assignment references it — deactivate it instead.`
        }
        confirmLabel="Delete"
        destructive
        loading={deleteMutation.isPending}
        onConfirm={() => {
          if (deleting !== null) {
            deleteMutation.mutate(deleting.id, { onSettled: () => setDeleting(null) });
          }
        }}
        onCancel={() => setDeleting(null)}
      />
    </>
  );
}
